// Automated Version & Cache Management for MenuTide
// Automatically checks for new deployments and forces a clean refresh for users seamlessly.

const CURRENT_BUILD_TIME = Date.now().toString();
const STORED_BUILD_KEY = 'menutide_app_build';
const LAST_CHECK_KEY = 'menutide_last_version_check';
const CHECK_INTERVAL_MS = 60 * 1000; // Check at most once a minute

export async function clearAllLocalCaches() {
  try {
    if (typeof window !== 'undefined' && 'caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map(k => window.caches.delete(k)));
    }
  } catch (e) {
    console.warn('[Cache] Could not clear CacheStorage:', e);
  }
}

export async function checkAndEnforceLatestVersion() {
  if (typeof window === 'undefined') return;

  try {
    // 1. Initial launch check: store our current bundle timestamp if missing
    const storedBuild = window.localStorage.getItem(STORED_BUILD_KEY);
    if (!storedBuild) {
      window.localStorage.setItem(STORED_BUILD_KEY, CURRENT_BUILD_TIME);
    }

    // 2. Fetch version.json from server with cache-busting timestamp
    const res = await fetch(`/version.json?_t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });

    if (!res.ok) return;

    const serverData = await res.json();
    const serverBuild = serverData?.buildTime;

    if (serverBuild && storedBuild && serverBuild !== storedBuild) {
      console.log(`[App Update] New version detected (${serverBuild} vs ${storedBuild}). Purging caches and reloading...`);
      window.localStorage.setItem(STORED_BUILD_KEY, serverBuild);
      
      // Clear any service worker / CacheStorage
      await clearAllLocalCaches();

      // Trigger automatic reload to load newest assets
      window.location.reload(true);
    } else if (serverBuild && !storedBuild) {
      window.localStorage.setItem(STORED_BUILD_KEY, serverBuild);
    }
  } catch (err) {
    // Network offline or failed check, continue running smoothly
    console.debug('[App Update Check] Skipped:', err.message);
  }
}

export function initAutoVersionChecker() {
  if (typeof window === 'undefined') return;

  // 1. Check on initial page load / launch
  checkAndEnforceLatestVersion();

  // 2. Check whenever user switches tabs back to MenuTide
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const lastCheck = parseInt(window.sessionStorage.getItem(LAST_CHECK_KEY) || '0', 10);
      if (Date.now() - lastCheck > CHECK_INTERVAL_MS) {
        window.sessionStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
        checkAndEnforceLatestVersion();
      }
    }
  });

  // 3. Periodic background check every 5 minutes
  setInterval(checkAndEnforceLatestVersion, 5 * 60 * 1000);
}
